import React from 'react';
import {
  Box,
  Card,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { motion } from 'framer-motion';
import { glassStyle } from '../theme/luxuryTheme';
import { useProductMix } from '../hooks/useAnalytics';

interface ProductMixProps {
  date?: string;
  storeId?: string;
}

const ProductMix: React.FC<ProductMixProps> = ({ date, storeId }) => {
  const theme = useTheme();
  const { data, isLoading, error } = useProductMix(date, storeId);

  const COLORS = ['#af2326', '#c94144', '#8a1a1c', '#d96166', '#6b1517'];

  if (isLoading) {
    return (
      <Card sx={{ ...glassStyle, p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ ...glassStyle, p: 4 }}>
        <Typography color="error">Failed to load product mix data</Typography>
      </Card>
    );
  }

  // Handle both old format (categoryBreakdown) and new format (categories array)
  let chartData: Array<{
    name: string;
    value: number;
    percentage?: number;
  }> = [];
  
  if (data.categories && Array.isArray(data.categories)) {
    // New format from hybrid API
    chartData = data.categories.map((cat: any) => ({
      name: cat.category,
      value: cat.totalRevenue || 0,
      percentage: cat.percentage || 0,
    }));
  } else if (data.categoryBreakdown) {
    // Old format
    chartData = Object.entries(data.categoryBreakdown || {}).map(([name, value]) => ({
      name,
      value: value as number,
    }));
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ ...glassStyle, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Product Mix Analysis
        </Typography>
        
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => <span style={{ color: theme.palette.text.primary }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {((data.topItems && data.topItems.length > 0) || (data.topSellingItems && data.topSellingItems.length > 0)) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Top Selling Items
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(data.topItems || data.topSellingItems || []).slice(0, 5).map((item: any, index: number) => (
                <Box
                  key={item.id || item.plu || index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  }}
                >
                  <Typography variant="body2">
                    {index + 1}. {item.itemName || item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                    {item.quantitySold ? `${item.quantitySold} sold` : `$${(item.totalRevenue || item.price || 0).toFixed(2)}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Card>
    </motion.div>
  );
};

export default ProductMix;